import { useEffect } from "react";
import { businessSchema, faqSchema, breadcrumbSchema } from "./schema";

/* Absolute origin the site is served from. Falls back to the live
   origin so it stays correct if the domain changes. */
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined"
    ? window.location.origin + (process.env.PUBLIC_URL || "")
    : "")
).replace(/\/$/, "");

function upsert(tag, attr, key, value, content) {
  if (!value) return;
  let el = document.head.querySelector(`${tag}[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(content, value);
}

function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const el = existing || document.createElement("script");
  el.id = id;
  el.type = "application/ld+json";
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
}

/**
 * Owns every per-page head tag: title, description, canonical,
 * Open Graph, Twitter, and JSON-LD.
 *
 * NOTE: these are injected client-side. Google renders JavaScript and
 * will see them; most social scrapers (WhatsApp, Facebook, LinkedIn)
 * do NOT, and read only the static index.html. The static fallbacks in
 * public/index.html exist for exactly that reason. Prerendering is the
 * real fix — see the SEO notes in README.
 */
export default function Seo({
  title,
  description,
  pathname,
  label,
  includeFaq = false,
  noindex = false,
}) {
  useEffect(() => {
    const url = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

    document.title = title;

    upsert("meta", "name", "description", description, "content");
    upsert("meta", "name", "robots", noindex ? "noindex, follow" : "index, follow", "content");

    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Open Graph
    upsert("meta", "property", "og:title", title, "content");
    upsert("meta", "property", "og:description", description, "content");
    upsert("meta", "property", "og:url", url, "content");
    upsert("meta", "property", "og:type", "website", "content");
    upsert("meta", "property", "og:site_name", "Dr. Aakash Sharma — Physiotherapist", "content");
    upsert("meta", "property", "og:locale", "en_IN", "content");
    upsert("meta", "property", "og:image", `${SITE_URL}/og-image.jpg`, "content");

    // Twitter
    upsert("meta", "name", "twitter:card", "summary_large_image", "content");
    upsert("meta", "name", "twitter:title", title, "content");
    upsert("meta", "name", "twitter:description", description, "content");
    upsert("meta", "name", "twitter:image", `${SITE_URL}/og-image.jpg`, "content");

    // structured data
    upsertJsonLd("ld-business", businessSchema(SITE_URL));
    upsertJsonLd("ld-breadcrumb", breadcrumbSchema(SITE_URL, pathname, label));
    upsertJsonLd("ld-faq", includeFaq ? faqSchema() : null);
  }, [title, description, pathname, label, includeFaq, noindex]);

  return null;
}
